import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import c3 from "c3";
import { ResizeSensor } from "css-element-queries";
import debounce from "lodash.debounce";

import UIPrimaryWidgetBox from "components/UI/PrimaryWidgetBox";
import theme from "theme";
import { kFormatter } from "utils";

const Container = styled.div.attrs({ className: "usage-payments-chart" })`
  height: 450px;

  .widget {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .chart {
    flex: 1;
  }
  .c3-axis-y .domain,
  .c3-axis-y2 .domain,
  .c3-axis .tick line {
    stroke: none;
  }
  .c3-grid line,
  .c3-axis-x .domain {
    stroke: ${props => props.theme.colors.paleskyLighter};
  }
  .c3-axis .tick tspan {
    fill: ${props => props.theme.colors.makoLighter};
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
  }

  .head {
    align-items: center;
    border-bottom: ${props => `1px solid ${props.theme.colors.silver}`};
    display: flex;
    justify-content: space-between;
    padding: 0 0 16px;
  }

  .title {
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .option {
    background: transparent;
    border-radius: 5px;
    border: none;
    color: ${props => props.theme.colors.makoLighter};
    cursor: pointer;
    font-family: Roboto;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1px;
    outline: none;
    padding: 6px 10px;
    text-transform: uppercase;
  }
  .option:hover,
  .option.active {
    background: ${props => props.theme.colors.silver};
    color: ${props => props.theme.colors.mako};
  }
`;

const chartConfig = {
  bindto: "#up-chart",
  data: {
    x: "x",
    columns: [
      [
        "x",
        "2019-01-01",
        "2019-02-01",
        "2019-03-01",
        "2019-04-01",
        "2019-05-01",
        "2019-06-01",
        "2019-07-01",
        "2019-08-01"
      ],
      ["Usage", 180, 240, 325, 375, 450, 410, 510, 220],
      ["Payments", 500, 1750, 1800, 4900, 3200, 3100, 2800, 2300]
    ],
    type: "bar",
    colors: {
      Usage: theme.colors.vividBlue,
      Payments: theme.colors.softCyan
    },
    axes: {
      Usage: "y",
      Payments: "y2"
    }
  },
  axis: {
    y2: {
      show: true,
      tick: {
        format: x => `$${kFormatter(x)}`
      }
    },
    x: {
      type: "timeseries",
      tick: {
        format: x => x.toLocaleDateString("en-US", { month: "short" })
      }
    }
  },
  grid: {
    y: {
      show: true
    }
  }
};

const AnalyticsDashboardUsagePaymentsChart = ({ className }) => {
  const containerRef = useRef(null);
  const [chart, setChart] = useState(null);

  useEffect(
    () => {
      if (containerRef.current && chart) {
        const rs = new ResizeSensor(
          containerRef.current,
          debounce(() => {
            chart.resize();
          }, 200)
        );
        return () => rs.detach();
      }
    },
    [containerRef, chart]
  );

  useEffect(() => {
    const aChart = c3.generate(chartConfig);
    setChart(aChart);
  }, []);

  return (
    <Container className={className} innerRef={containerRef}>
      <UIPrimaryWidgetBox className="widget p">
        <div className="head">
          <div className="title">Usage / Payments Breakdown</div>
          <div className="options">
            <button className="option mr">All</button>
            <button className="option mr active">Monthly</button>
            <button className="option mr">Year to Date</button>
            <button className="option mr">Rolling Year</button>
          </div>
        </div>
        <div className="chart mt" id="up-chart" />
      </UIPrimaryWidgetBox>
    </Container>
  );
};

export default AnalyticsDashboardUsagePaymentsChart;
